import React, { useState, useEffect } from 'react';
import { Music } from 'lucide-react';

interface LoadingCinematicProps {
  onComplete: () => void;
}

const LoadingCinematic: React.FC<LoadingCinematicProps> = ({ onComplete }) => {
  const [stage, setStage] = useState(0);

  useEffect(() => {
    // Séquence optimisée des animations
    const timeouts = [
      setTimeout(() => setStage(1), 600),   // Logo apparaît
      setTimeout(() => setStage(2), 1800),  // Titre principal
      setTimeout(() => setStage(3), 3200),  // Sous-titre
      setTimeout(() => setStage(4), 4500),  // Effets finaux
      setTimeout(() => setStage(5), 5800),  // Fade out
      setTimeout(() => onComplete(), 6200), // Fin
    ];

    return () => {
      timeouts.forEach(timeout => clearTimeout(timeout));
    };
  }, [onComplete]);

  return (
    <div className={`fixed inset-0 z-[100] bg-black flex items-center justify-center transition-opacity duration-700 ${
      stage === 5 ? 'opacity-0' : 'opacity-100'
    }`}>
      {/* Fond avec effets optimisés */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Rayons simplifiés */}
        <div className={`absolute inset-0 transition-opacity duration-1000 ${stage >= 1 ? 'opacity-100' : 'opacity-0'}`}>
          <div className="absolute top-1/2 left-1/2 w-80 h-1 bg-gradient-to-r from-transparent via-yellow-400/20 to-transparent transform -translate-x-1/2 -translate-y-1/2 animate-spin" style={{animationDuration: '12s'}}></div>
        </div>
        
        {/* Piano qui se dessine */}
        <div className={`absolute inset-0 flex items-center justify-center transition-opacity duration-2000 ${stage >= 1 ? 'opacity-100' : 'opacity-0'}`}>
          <div className="piano-drawing">
            {/* Corps du piano */}
            <div className="piano-body"></div>
            {/* Touches blanches */}
            <div className="piano-keys">
              {[...Array(14)].map((_, i) => (
                <div key={i} className="white-key" style={{animationDelay: `${1 + i * 0.2}s`}}></div>
              ))}
            </div>
            {/* Touches noires */}
            <div className="piano-black-keys">
              {[...Array(10)].map((_, i) => (
                <div key={i} className="black-key" style={{animationDelay: `${2.8 + i * 0.1}s`}}></div>
              ))}
            </div>
          </div>
        </div>

        {/* Particules réduites */}
        <div className={`absolute inset-0 transition-opacity duration-1000 ${stage >= 2 ? 'opacity-100' : 'opacity-0'}`}>
          {[...Array(8)].map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 bg-yellow-400/60 rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animation: `float-simple 8s ease-in-out infinite ${Math.random() * 3}s`,
                opacity: 0.3 + Math.random() * 0.3
              }}
            ></div>
          ))}
        </div>

        {/* Vinyle qui tourne */}
        <div className={`absolute inset-0 flex items-center justify-center transition-opacity duration-1500 ${stage >= 1 ? 'opacity-100' : 'opacity-0'}`}>
          <div className="vinyl-record">
            <div className="vinyl-outer"></div>
            <div className="vinyl-inner"></div>
            <div className="vinyl-center"></div>
            <div className="vinyl-label">MB</div>
          </div>
        </div>
      </div>

      <div className="text-center relative z-10">
        {/* Logo optimisé */}
        <div className={`mb-12 transition-all duration-1200 ${
          stage >= 1 ? 'opacity-100 scale-100' : 'opacity-0 scale-75'
        }`}>
          <div className="relative">
            <Music className="w-24 h-24 text-yellow-400 mx-auto" style={{filter: 'drop-shadow(0 0 25px rgba(212, 175, 55, 0.6))'}} />
          </div>
        </div>

        {/* Titre principal optimisé */}
        <div className={`mb-8 transition-all duration-1500 ${
          stage >= 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <h1 className="text-6xl md:text-8xl font-light mb-4 relative">
            <span className="text-shimmer-cinematic block">
              Musique
            </span>
            <span className="heading-accent font-semibold block mt-2">
              Buchta
            </span>
          </h1>
        </div>

        {/* Sous-titre optimisé */}
        <div className={`mb-10 transition-all duration-1200 ${
          stage >= 3 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <p className="text-xl md:text-3xl text-white/70 font-light tracking-wide">
            L'exigence du son et du savoir-faire
          </p>
          <div className="w-48 h-px bg-gradient-to-r from-transparent via-yellow-400/60 to-transparent mx-auto mt-4"></div>
        </div>

        {/* Notes simplifiées */}
        <div className={`absolute inset-0 pointer-events-none transition-opacity duration-1000 ${
          stage >= 3 ? 'opacity-100' : 'opacity-0'
        }`}>
          {['♪', '♫', '♬'].map((note, i) => (
            <div
              key={i}
              className="absolute text-yellow-400/40 text-4xl font-bold"
              style={{
                left: `${25 + i * 20}%`,
                top: `${30 + (i % 2) * 40}%`,
                animation: `dance-note-simple 6s ease-in-out infinite ${i * 1.2}s`
              }}
            >
              {note}
            </div>
          ))}
        </div>

        {/* Effet final simplifié */}
        <div className={`absolute inset-0 transition-opacity duration-1000 ${
          stage >= 4 ? 'opacity-100' : 'opacity-0'
        }`}>
          <div className="absolute top-1/2 left-1/2 w-80 h-80 bg-gradient-radial from-yellow-400/15 to-transparent rounded-full transform -translate-x-1/2 -translate-y-1/2"></div>
        </div>
      </div>

      {/* Transition finale */}
      {stage === 5 && (
        <div className="absolute inset-0 bg-black"></div>
      )}
    </div>
  );
};

export default LoadingCinematic;